/*
 * Copyright 2022 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { makeFieldSchema } from '../../../extensions/types';

import { z } from 'zod';

export const EntitySelectionPickerSchemaZod = z
  .object({
    'ui:fieldName': z.string(),
    'ui:property': z.string().optional(),
    mapTo: z.record(z.any()).or(z.string()).optional(),
    'ui:displayLabel': z.string().optional(),
  })
  .refine(
    schema =>
      (schema['ui:property'] !== undefined && schema.mapTo === undefined) ||
      (schema['ui:property'] === undefined && schema.mapTo !== undefined),
    {
      message:
        "You must define at maximum one between 'ui:property' and 'mapTo' configurations",
    },
  );

export type EntitySelectionPickerSchema = z.infer<
  typeof EntitySelectionPickerSchemaZod
>;

/**
 * @public
 */
export const EntitySelectionPickerFieldSchema = makeFieldSchema({
  output: zoid => zoid.string(),
  uiOptions: zoid =>
    zoid.object({
      allowArbitraryValues: zoid
        .boolean()
        .optional()
        .describe('Whether to allow arbitrary user input. Defaults to true'),
    }),
});

/**
 * The input props that can be specified under `ui:options` for the
 * `EntitySelectionPicker` field extension.
 *
 * @public
 */
export type EntitySelectionPickerUiOptions = NonNullable<
  typeof EntitySelectionPickerFieldSchema.TProps.uiSchema['ui:options']
>;

export type EntitySelectionPickerProps =
  typeof EntitySelectionPickerFieldSchema.TProps;
